/**
 * Gallery read/write API — static JSON + /api/gallery admin routes.
 * Speed: browser ETag revalidation + IndexedDB snapshot (SWR).
 */
import { GALLERY_API, GALLERY_BASE } from "./galleryConstants";
import { likedIdsWithOverrides } from "./likedOverrides";
import { loadTsToken } from "../../../lib/tsIdentity";
import { UD_PAGE_SIZE } from "./uncategorizedDesigns";

const JSON_CACHE = new Map();
const CACHE_MS = 5 * 60 * 1000;

const IDB_NAME = "ttt_td_gallery";
const IDB_VER = 1;
const IDB_STORE = "snapshots";
const IDB_KEY = "bootstrap_v1";

function openIdb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") {
      reject(new Error("no indexedDB"));
      return;
    }
    const req = indexedDB.open(IDB_NAME, IDB_VER);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(IDB_STORE)) {
        db.createObjectStore(IDB_STORE);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error || new Error("idb open failed"));
  });
}

export async function readBootstrapSnapshot() {
  try {
    const db = await openIdb();
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readonly");
      const req = tx.objectStore(IDB_STORE).get(IDB_KEY);
      req.onsuccess = () => {
        const row = req.result;
        resolve(row?.data || null);
      };
      req.onerror = () => reject(req.error);
    });
  } catch {
    return null;
  }
}

export async function writeBootstrapSnapshot(data) {
  try {
    const db = await openIdb();
    await new Promise((resolve, reject) => {
      const tx = db.transaction(IDB_STORE, "readwrite");
      tx.objectStore(IDB_STORE).put({ data, at: Date.now() }, IDB_KEY);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  } catch {
    /* private mode / quota — ignore */
  }
}

/**
 * Snapshot read-modify-writes are serialized: two quick heart taps otherwise
 * both read the same pre-toggle row and the second write drops the first
 * like from the offline bootstrap.
 */
let snapshotWriteChain = Promise.resolve();

function queueSnapshotWrite(work) {
  const run = snapshotWriteChain.then(work, work);
  snapshotWriteChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

/**
 * Fold one confirmed like/unlike into the IndexedDB bootstrap snapshot.
 *
 * The snapshot is what paints the grid on the next open, before any network
 * read, so leaving it at the pre-toggle liked list would show a stale heart
 * even though the write succeeded.
 *
 * @returns {Promise<boolean>} false when there is no snapshot to patch
 */
export function patchBootstrapSnapshotLiked(photoId, liked) {
  return queueSnapshotWrite(async () => {
    const snap = await readBootstrapSnapshot();
    if (!snap) return false;
    const likedIds = likedIdsWithOverrides(snap.likedIds, [[photoId, Boolean(liked)]]);
    await writeBootstrapSnapshot({ ...snap, likedIds });
    return true;
  });
}

/**
 * Fetch JSON with in-memory TTL + If-None-Match.
 * Returns { data, unchanged } so callers can skip expensive rebuilds on 304 / warm cache.
 */
async function fetchJson(path, { bust = false } = {}) {
  const key = path;
  const cached = JSON_CACHE.get(key);
  if (!bust && cached && Date.now() - cached.at < CACHE_MS) {
    return { data: cached.data, unchanged: true };
  }
  const url = bust ? `${path}?t=${Date.now()}` : path;
  const headers = {};
  if (!bust && cached?.etag) {
    headers["If-None-Match"] = cached.etag;
  }
  const res = await fetch(url, {
    cache: bust ? "no-store" : "default",
    headers,
  });
  if (res.status === 304 && cached?.data != null) {
    cached.at = Date.now();
    return { data: cached.data, unchanged: true };
  }
  if (!res.ok) throw new Error(`Could not load ${path}`);
  const data = await res.json();
  const etag = res.headers.get("ETag") || cached?.etag || null;
  JSON_CACHE.set(key, { data, at: Date.now(), etag });
  return { data, unchanged: false };
}

export function bustGalleryCache() {
  JSON_CACHE.clear();
}

export async function fetchGalleryBootstrap({ bust = false } = {}) {
  const [catalogR, libraryR, likedR, schemaR] = await Promise.all([
    fetchJson(`${GALLERY_BASE}/catalog.json`, { bust }),
    fetchJson(`${GALLERY_BASE}/design-library.json`, { bust }),
    fetchJson(`${GALLERY_BASE}/liked-design-ids.json`, { bust }),
    fetchJson(`${GALLERY_BASE}/field-schema.json`, { bust }).catch(() => ({
      data: { fields: [] },
      unchanged: true,
    })),
  ]);
  const unchanged =
    catalogR.unchanged && libraryR.unchanged && likedR.unchanged && schemaR.unchanged;
  return {
    catalog: catalogR.data,
    library: libraryR.data,
    likedIds: likedR.data,
    fieldSchema: schemaR.data,
    unchanged,
  };
}

function authHeaders(extra = {}) {
  const token = loadTsToken();
  return {
    ...extra,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export async function patchGalleryPhoto(body, { keepalive = false } = {}) {
  const res = await fetch(`${GALLERY_API}/photo`, {
    method: "PATCH",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify(body),
    keepalive,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not save design");
  return data;
}

/**
 * TT-702 Phase 8 — has what staff saved actually reached the customer catalog?
 *
 * Read-only, and deliberately not a trigger: a staff write already queues its
 * own sync, so polling this must not queue another one. Uses the same bearer
 * as every other staff call; a 401/403 surfaces as a thrown error and the
 * badge reports "could not confirm" rather than inventing a state.
 *
 * @returns {Promise<{ok: boolean, version: number, active_design_count: number,
 *   synced: boolean, synced_at: string|null,
 *   sync: {running: boolean, pending: boolean, uncovered: boolean}}>}
 */
export async function fetchCatalogStatus() {
  const res = await fetch(`${GALLERY_API}/catalog-status`, {
    method: "GET",
    headers: authHeaders(),
    cache: "no-store",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not check the customer catalog");
  return data;
}

/**
 * TT-702 Phase 10 — the library's quality queues, resolved server-side.
 *
 * Read-only in the strongest sense: it resolves state and changes none, so it
 * neither writes a status nor triggers a sync. Uses the same bearer as every
 * other staff call; a 401/403 surfaces as a thrown error and the panel says it
 * could not load rather than rendering an empty library as a clean one.
 *
 * @param {{limit?: number}} [opts] how many members of each queue to name;
 *   the backend clamps it and always reports the full count beside them.
 * @returns {Promise<object>} the summary payload, see design_quality.py
 */
export async function fetchDesignQuality({ limit } = {}) {
  const query = limit == null ? "" : `?limit=${encodeURIComponent(limit)}`;
  const res = await fetch(`${GALLERY_API}/quality-summary${query}`, {
    method: "GET",
    headers: authHeaders(),
    cache: "no-store",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not load design quality");
  return data;
}

/**
 * TT-850 — one read-only page of designs with no resolved customer category.
 *
 * Read-only, all the way down: resolves state, changes none. Uses the same
 * bearer as every other staff call; a 401/403 surfaces as a thrown error, and
 * the page reports "could not load" rather than an empty (falsely
 * reassuring) list.
 *
 * @param {{offset?: number, limit?: number}} [opts]
 * @returns {Promise<{ok: boolean, total: number, offset: number, limit: number,
 *   items: object[]}>} see uncategorized_designs.py for item shape
 */
export async function fetchUncategorizedDesigns({ offset = 0, limit = UD_PAGE_SIZE } = {}) {
  const params = new URLSearchParams({ offset: String(offset), limit: String(limit) });
  const res = await fetch(`${GALLERY_API}/uncategorized-designs?${params.toString()}`, {
    method: "GET",
    headers: authHeaders(),
    cache: "no-store",
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not load uncategorized designs");
  return data;
}

/**
 * Persist one staff-typed custom option to a multiselect field's shared
 * option list. Backend dedupes case-insensitively and returns the
 * canonical spelling (existing or newly created).
 *
 * @param {string} fieldKey
 * @param {string} value
 * @returns {Promise<{ ok: boolean, field_key: string, value: string, created: boolean }>}
 */
export async function addFieldOption(fieldKey, value) {
  const res = await fetch(`${GALLERY_API}/fields/option`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ field_key: fieldKey, value }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not save option");
  return data;
}

/**
 * Like/unlike exactly one design — the path the gallery UI uses.
 *
 * The whole-list PUT below sends the list as this tab last saw it, so two staff
 * hearting two different designs at the same moment lose one of the two
 * decisions. The delta is applied server-side under the publication lock, so
 * both land.
 *
 * @param {number|string} photoId
 * @param {boolean} liked
 * @returns {Promise<{ok: boolean, id: number, liked: boolean, changed: boolean, total: number}>}
 */
export async function postLikedIdDelta(photoId, liked) {
  const res = await fetch(`${GALLERY_API}/liked-ids/delta`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ id: Number(photoId), liked: Boolean(liked) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not update liked designs");
  return data;
}

/**
 * Whole-list write, kept for the still-supported PUT /api/gallery/liked-ids
 * route (the auto-publish cron writes there). No Manage surface calls this
 * any more — hearts go through postLikedIdDelta.
 */
export async function putLikedIds(ids) {
  const res = await fetch(`${GALLERY_API}/liked-ids`, {
    method: "PUT",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ ids: ids.map((id) => Number(id)) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not update liked designs");
  return data;
}

export async function deleteGalleryPhoto(photoId) {
  const res = await fetch(`${GALLERY_API}/photo`, {
    method: "DELETE",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ id: Number(photoId) }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not delete design");
  return data;
}

export async function generateAiPinkPreview(photoId, { force = false } = {}) {
  const res = await fetch(`${GALLERY_API}/ai-pink-preview`, {
    method: "POST",
    headers: authHeaders({ "Content-Type": "application/json" }),
    body: JSON.stringify({ photo_id: Number(photoId), force }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not generate AI pink preview");
  return data;
}

export async function uploadGalleryPhoto({ category, subpath = "", file }) {
  const form = new FormData();
  form.append("category", category);
  form.append("subpath", subpath);
  form.append("file", file);
  const res = await fetch(`${GALLERY_API}/upload`, {
    method: "POST",
    headers: authHeaders(),
    body: form,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.detail || "Could not upload image");
  return data;
}

/**
 * Replace a single gallery photo's image file with progress reporting.
 *
 * Uses XMLHttpRequest (not fetch) so we get real upload-progress events.
 * Form fields match the backend /api/gallery/replace endpoint:
 *   - id    : int  (the photo/design id)
 *   - file  : File (the prepared image)
 *
 * @param {number|string} photoId
 * @param {File|Blob} file  — prepared image from prepareGalleryImage()
 * @param {{ onProgress?: (loaded:number,total:number)=>void, signal?: AbortSignal, timeout?: number }} [opts]
 * @returns {Promise<{ ok: boolean, photo: object, image_version: number }>}
 */
export function replaceGalleryPhoto(photoId, file, opts = {}) {
  const { onProgress, signal, timeout = 60_000 } = opts;

  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const form = new FormData();
    form.append("id", String(Number(photoId)));
    form.append("file", file);

    xhr.open("POST", `${GALLERY_API}/replace`);
    const token = loadTsToken();
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.responseType = "json";
    xhr.timeout = timeout;

    let onAbort = null;

    const cleanup = () => {
      xhr.onload = null;
      xhr.onerror = null;
      xhr.ontimeout = null;
      xhr.onabort = null;
      xhr.upload.onprogress = null;
      if (signal && onAbort) signal.removeEventListener("abort", onAbort);
    };

    const parseResponse = () => {
      let data = xhr.response;
      if (data == null) {
        try {
          data = JSON.parse(xhr.responseText || "{}");
        } catch {
          data = {};
        }
      }
      return data;
    };

    // ── completion ───────────────────────────────────────────────────
    xhr.onload = () => {
      const status = xhr.status;
      const data = parseResponse();
      cleanup();
      if (status >= 200 && status < 300) {
        resolve(data);
      } else {
        reject(new Error(data?.detail || `Replace failed (${status})`));
      }
    };

    xhr.onerror = () => {
      cleanup();
      reject(new Error("Network error during image upload"));
    };

    xhr.ontimeout = () => {
      cleanup();
      reject(new Error("Image upload timed out"));
    };

    xhr.onabort = () => {
      cleanup();
      reject(new DOMException("Image upload aborted", "AbortError"));
    };

    // ── upload progress ──────────────────────────────────────────────
    if (onProgress) {
      xhr.upload.onprogress = (e) => {
        if (e.lengthComputable) {
          onProgress(e.loaded, e.total);
        }
      };
    }

    // ── abort signal ────────────────────────────────────────────────
    if (signal) {
      if (signal.aborted) {
        reject(new DOMException("Image upload aborted", "AbortError"));
        return;
      }
      onAbort = () => xhr.abort();
      signal.addEventListener("abort", onAbort, { once: true });
    }

    xhr.send(form);
  });
}
